import { useEffect } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  InternalSidepanel,
  PermissionEnum,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  BlacklistDataContent,
  BlacklistUserInfo,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearTransactionState,
  setPanelIsOpen,
} from '../../store/slices/problem-transactions/internal-transaction';
import useProblemInternalTransaction from './hooks/use-internal';

const ProblemInternalTransactionHOC = ({
  matchPath,
  backPath,
  generalPath,
  amlPath,
  logPath,
  warningPath,
  changeLogPath,
  chatPath,
  documentPath,
  commentsPath,
}: {
  matchPath: string;
  backPath: string;
  generalPath: string;
  amlPath: string;
  logPath: string;
  warningPath: string;
  changeLogPath: string;
  chatPath: string;
  documentPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const match = useMatch(matchPath);

  const {
    documents,
    document: transactionDocument,
    transaction,
    panelIsOpen,
    blacklistDataVisible,
    blacklistData,
    blacklistItemVisible,
    blacklistItem,
  } = useAppSelector((state) => state.customersProblemTransactions.internal);
  const { currencies: currenciesList, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const { customerId, transactionId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    handleClose,
    transactionIsLoading,
    isLoadingAml,
    isLoadingLog,
    warningsIsLoading,
    isFetching,
    getTransactionDocumentyIdAction,
    downloadInProgress,
    downloadIsFetching,
    imgLink,
    onDeleteDocument,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    onMoveDocument,
    documentCategoryChangeInProgress,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteComment,
    deleteCommentInProgress,
    commentInProgress,
    documentStatusChangeInProgress,
    onChangeCustomerDocumentsStatus,
    downloadFileIsSuccess,
    documentIsLoading,
    documentIsFetching,
    handleCloseBlacklistData,
    getInternalTransactionById,
  } = useProblemInternalTransaction({
    transactionId,
    activePermissions,
    documents,
    transactionDocument,
    backPath,
    customerId,
    transaction,
  });

  useEffect(() => {
    dispatch(setPanelIsOpen(true));

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  useEffect(() => {
    if (customerId && !transaction && transactionId) {
      getInternalTransactionById();
    }
  }, [customerId, transaction, transactionId, getInternalTransactionById]);

  return (
    <>
      <InternalSidepanel
        panelIsOpen={panelIsOpen}
        handleClose={handleClose}
        isLoading={
          transactionIsLoading ||
          isLoadingAml ||
          isLoadingLog ||
          warningsIsLoading ||
          isFetching
        }
        title="Internal Transaction"
        tabs={{
          warningPath: generatePath(`${warningPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          generalPath: generatePath(`${generalPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          amlPath: generatePath(`${amlPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          logPath: generatePath(`${logPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          chatPath: generatePath(`${chatPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          documentsPath: generatePath(`${documentPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          commentsPath: generatePath(`${commentsPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
        }}
        isWarning
        getTransactionDocument={getTransactionDocumentyIdAction}
        enableEdit={
          !activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
          )
        }
        isCreateForm={
          !activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
          )
        }
        createAllowed={activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
        )}
        document={transactionDocument}
        documentFileIsLoading={downloadInProgress || downloadIsFetching}
        downloadInProgress={downloadInProgress || downloadIsFetching}
        imgLink={imgLink}
        onDelete={onDeleteDocument}
        deleteInProgress={deleteDocumentInProgress}
        deleteIsSuccess={deleteDocumentIsSuccess}
        onMove={onMoveDocument}
        moveInProgress={documentCategoryChangeInProgress}
        onDownload={onDownloadDocument}
        sendComment={onSendDocumentComment}
        editComment={onEditComment}
        editCommentInProgress={editCommentInProgress}
        editCommentIsSuccess={editCommentIsSuccess}
        deleteComment={onDeleteComment}
        deleteCommentInProgress={deleteCommentInProgress}
        commentInProgress={commentInProgress}
        documentStatusChangeInProgress={documentStatusChangeInProgress}
        changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
        onDownloadSuccess={downloadFileIsSuccess}
        documentIsLoading={documentIsLoading || documentIsFetching}
        activePermission={activePermissions}
        currenciesList={currenciesList}
        transactionInfo={transaction}
        sendMessage={sendMessage}
        sidepanelExpandedKey="problem-internal"
      />
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-internal-blacklist"
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisible}
        onClose={handleCloseBlacklistData}
        title="User Details"
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-internal-blacklist"
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default ProblemInternalTransactionHOC;
